import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Center, Divider, Grid, GridItem, PopoverBody, rem, Spinner, Stack, Text, theme } from '@bonusly/design-system';
import { useRecognizeURL } from '@bonusly/url';
import { useUser } from '@bonusly/user-queries';
import { Permission, useCurrentUser, usePermission } from '@bonusly/who-am-i';

import { UserAvatar } from '../components/user-avatar';
import { UserFollowButton } from '../components/user-follow-button';
import { UserPopoverActions } from '../components/user-popover-actions';
import { UserPopoverActivitySection } from '../components/user-popover-activity-section';
import { UserPopoverCustomProperties } from '../components/user-popover-custom-properties';
import { useConnectionData } from '../hooks/use-connection-data';
import { CelebrationLink } from './celebration-link';

interface UserPopoverContentProps {
	userId: string;
}

export const UserPopoverContent: FC<UserPopoverContentProps> = ({ userId }) => {
	const { t } = useTranslation('components');
	const { data: user } = useUser(userId);
	const { following = [], id: currentUserId } = useCurrentUser();
	const isFollowing = following.includes(userId);
	const isViewingSelf = userId === currentUserId;
	const userUrl = useRecognizeURL(`/company/users/${userId}`);

	const canAccessUserProfile = usePermission(Permission.CanAccessUserProfile);
	const canGiveRecognition = usePermission(Permission.CanGiveRecognition);

	const { userData: connectionData, isLoading: isLoadingActivityData } = useConnectionData(userId);

	const hasCelebrationsToday = connectionData && connectionData.celebrations_today ? connectionData.celebrations_today.length > 0 : false;

	if (!user) {
		return (
			<PopoverBody padding={5} zIndex={theme.zIndices.tooltip + 200}>
				<Center minH={rem(150)}>
					<Spinner />
				</Center>
			</PopoverBody>
		);
	}

	return (
		<PopoverBody padding={5} zIndex={theme.zIndices.tooltip + 200}>
			<Stack spacing={3}>
				<Grid alignItems="center" templateColumns="56px 1fr 60px" templateRows="minmax(28px, max-content) minmax(26px, max-content)" overflow="wrap" w="100%">
					<GridItem colSpan={1} rowSpan={2}>
						<UserAvatar border="2px solid white" size="xl" user={user} />
					</GridItem>

					<GridItem colSpan={2} pl="8px" pt="2px" textOverflow="wrap">
						<Text size="lg" fontWeight="medium">
							{user.displayName}
						</Text>
					</GridItem>

					<GridItem pl="8px" pb="2px" textOverflow="wrap" maxW="162px">
						<Text flex={1} fontSize="sm" fontWeight="normal" color="text.softer">
							@{user.username}
						</Text>
					</GridItem>

					{!isViewingSelf && (
						<GridItem pl="4px" pb="2px" alignSelf="end" justifySelf="end">
							<UserFollowButton isFollowing={isFollowing} userId={user.id} />
						</GridItem>
					)}
				</Grid>

				<UserPopoverCustomProperties department={user.customProperties.department} location={user.customProperties.location} />

				{!isViewingSelf && canGiveRecognition && (
					<>
						<Divider />

						<UserPopoverActivitySection
							isLoading={isLoadingActivityData}
							given={connectionData && connectionData.given}
							received={connectionData && connectionData.received}
						/>

						<UserPopoverActions user={user} />
					</>
				)}

				{isViewingSelf && canAccessUserProfile && (
					<>
						<Divider />
						<Button as="a" href={userUrl} w="full">
							{t('user_popover.button_text')}
						</Button>
					</>
				)}

				{hasCelebrationsToday && (
					<>
						<Divider />
						{connectionData?.celebrations_today.map((celebration) => (
							<CelebrationLink key={celebration.id} celebrationDate={celebration.date} celebrationId={celebration.id} celebrationType={celebration.type} />
						))}
					</>
				)}
			</Stack>
		</PopoverBody>
	);
};
